import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useSubscription } from '../contexts/SubscriptionContext'
import { useOrganizations } from '../contexts/OrganizationsContext'
import { collection, addDoc, query, where, getDocs, doc, setDoc, serverTimestamp, orderBy, deleteDoc } from 'firebase/firestore'
import { db } from '../services/firebase'
import groupService from '../services/groupService'
import groupAccessService from '../services/groupAccessService'
import subscriptionService from '../services/subscriptionService'
import organizationService from '../services/organizationService'
import { UserPlus, Plus, Users, MapPin, Phone, Mail, Settings, Eye, Edit, Trash2, Search, Shield, UserCheck, X, Lock, Heart } from 'lucide-react'
import toast from 'react-hot-toast'

export default function Groups() {
  const { currentUser, userProfile } = useAuth()
  const { subscription } = useSubscription()
  const { organizations } = useOrganizations()
  const [groups, setGroups] = useState([])
  const [loading, setLoading] = useState(true)
  const [showAddGroupModal, setShowAddGroupModal] = useState(false)
  const [canCreatePrivateGroups, setCanCreatePrivateGroups] = useState(false)

  const isEnterprisePlan = String(subscription?.planType || '').toLowerCase() === 'enterprise'

  // Check if user has permission to create groups
  const canCreateGroups = userProfile?.accessLevel !== 'standard' && 
                         (userProfile?.isOrganizationAdmin || userProfile?.isPlatformAdmin)
  const [editingGroup, setEditingGroup] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [showAccessModal, setShowAccessModal] = useState(false)
  const [selectedGroup, setSelectedGroup] = useState(null)
  const [groupAccessUsers, setGroupAccessUsers] = useState([])
  const [accessEmail, setAccessEmail] = useState('')
  const [groupForm, setGroupForm] = useState({
    name: '',
    description: '',
    organizationId: '',
    isPublic: true,
    isPrivate: false,
    maxMembers: 50,
    contactEmail: '',
    contactPhone: '',
    groupLeaderEmail: ''
  })

  useEffect(() => {
    // Redirect standard users away from Groups page
    if (userProfile?.accessLevel === 'standard') {
      console.log('🚫 Standard users cannot access Groups page')
      window.location.href = '/my-alerts'
      return
    }

    fetchGroups()
    
    // Debug logging
    console.log('🔍 Groups: userProfile:', userProfile)
    console.log('🔍 Groups: userProfile.organizationId:', userProfile?.organizationId)
    console.log('🔍 Groups: userProfile.organizations:', userProfile?.organizations)
    console.log('🔍 Groups: current groupForm.organizationId:', groupForm.organizationId)
    
    // Auto-set organization using centralized service
    const orgId = organizationService.getPrimaryOrganizationId(userProfile, 'Crisp Technologies LLC')
    
    if (orgId && !groupForm.organizationId) {
      console.log('✅ Groups: Auto-setting organization to:', orgId)
      setGroupForm(prev => ({
        ...prev,
        organizationId: orgId
      }))
    }
  }, [currentUser, organizations, userProfile])

  useEffect(() => {
    setCanCreatePrivateGroups(subscriptionService.arePrivateGroupsAllowed(subscription))
  }, [subscription])

  const fetchGroups = async () => {
    try {
      setLoading(true)
      
      if (!currentUser || !userProfile) {
        console.log('❌ Groups: Missing currentUser or userProfile')
        setGroups([])
        return
      }

      console.log('🔍 Groups: Fetching groups using organizationService')

      // Use centralized organization service
      const groupsData = await organizationService.getGroupsForUser(
        userProfile, 
        currentUser.uid, 
        'Crisp Technologies LLC'
      )
      
      setGroups(groupsData)
      console.log('✅ Groups: Loaded', groupsData.length, 'groups using organizationService')
    } catch (error) {
      console.error('❌ Groups: Error fetching groups:', error)
      toast.error('Failed to load groups')
      setGroups([])
    } finally {
      setLoading(false)
    }
  }

  const handleSubmitGroup = async (e) => {
    e.preventDefault()
    
    if (!groupForm.name.trim()) {
      toast.error('Group name is required')
      return
    }

    if (!groupForm.organizationId) {
      toast.error('Please select an organization')
      return
    }

    try {
      // Sanitize group name for use as document ID
      const sanitizedGroupName = (groupForm.name || '')
        .replace(/[^a-zA-Z0-9\s-_]/g, '')
        .replace(/\s+/g, '_')
        .toLowerCase()

      const groupData = {
        name: groupForm.name?.trim() || '',
        description: groupForm.description?.trim() || '',
        organizationId: groupForm.organizationId,
        isPublic: groupForm.isPublic ?? true,
        isPrivate: groupForm.isPrivate ?? false,
        maxMembers: isEnterprisePlan ? null : (Number.parseInt(String(groupForm.maxMembers), 10) || 50),
        contactEmail: groupForm.contactEmail?.trim() || '',
        contactPhone: groupForm.contactPhone?.trim() || '',
        groupLeaderEmail: groupForm.groupLeaderEmail?.trim() || '',
        updatedAt: serverTimestamp()
      }

      if (editingGroup) {
        // Update existing group - use original group ID
        groupData.updatedBy = typeof currentUser === 'string' ? currentUser : currentUser.uid
        groupData.updatedByName = userProfile?.displayName || (typeof currentUser === 'object' ? currentUser.displayName : 'Unknown')
        
        const groupDocRef = doc(db, 'organizations', groupForm.organizationId, 'groups', editingGroup.id)
        await setDoc(groupDocRef, groupData, { merge: true })
        toast.success('Group updated successfully!')
      } else {
        // Create new group using group name as document ID
        groupData.createdBy = typeof currentUser === 'string' ? currentUser : currentUser.uid
        groupData.createdByName = userProfile?.displayName || (typeof currentUser === 'object' ? currentUser.displayName : 'Unknown')
        groupData.memberCount = 1
        groupData.isActive = true
        groupData.createdAt = serverTimestamp()
        
        const groupDocRef = doc(db, 'organizations', groupForm.organizationId, 'groups', sanitizedGroupName)
        await setDoc(groupDocRef, groupData)
        console.log('✅ Group created with name as ID:', sanitizedGroupName)
        toast.success('Group created successfully!')
      }
      
      console.log('✅ Group saved:', sanitizedGroupName)
      
      // Reset form and close modal
      setGroupForm({
        name: '',
        description: '',
        organizationId: userProfile?.organizationId || '',
        isPublic: true,
        isPrivate: false,
        maxMembers: isEnterprisePlan ? '' : 50,
        contactEmail: '',
        contactPhone: '',
        groupLeaderEmail: ''
      })
      setEditingGroup(null)
      setShowAddGroupModal(false)
      
      // Refresh groups list
      fetchGroups()
      
    } catch (error) {
      console.error('❌ Error saving group:', error)
      toast.error(`Failed to ${editingGroup ? 'update' : 'create'} group`)
    }
  }

  const filteredGroups = groups.filter(group => {
    const matchesSearch = group.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         group.description.toLowerCase().includes(searchTerm.toLowerCase())

    return matchesSearch
  })

  const getOrganizationName = (organizationId) => {
    const org = organizations.find(o => o.id === organizationId)
    return org?.name || 'Unknown Organization'
  }

  const handleEditGroup = (group) => {
    setEditingGroup(group)
    setGroupForm({
      name: group.name,
      description: group.description,
      organizationId: group.organizationId,
      isPublic: group.isPublic ?? true,
      isPrivate: group.isPrivate ?? false,
      maxMembers: isEnterprisePlan ? '' : (group.maxMembers ?? 50),
      contactEmail: group.contactEmail,
      contactPhone: group.contactPhone,
      groupLeaderEmail: group.groupLeaderEmail || ''
    })
    setShowAddGroupModal(true)
  }

  const handleDeleteGroup = async (group) => {
    if (!window.confirm(`Are you sure you want to delete "${group.name}"? This action cannot be undone.`)) {
      return
    }

    try {
      const orgId =
        organizationService.coerceOrganizationId(group.organizationId) ||
        organizationService.getPrimaryOrganizationId(userProfile, 'Crisp Technologies LLC')

      if (!orgId) {
        toast.error('Unable to determine organization')
        return
      }

      const groupDocId = group.id
      if (!groupDocId) {
        toast.error('Invalid group')
        return
      }

      console.log('🗑️ Deleting group doc:', { orgId, groupDocId, name: group.name })
      await deleteDoc(doc(db, 'organizations', orgId, 'groups', groupDocId))

      toast.success('Group deleted successfully!')
      fetchGroups()
    } catch (error) {
      console.error('❌ Error deleting group:', error)
      toast.error('Failed to delete group')
    }
  }

  // Group access management functions
  const handleManageAccess = async (group) => {
    setSelectedGroup(group)
    setShowAccessModal(true)
    
    try {
      const accessUsers = await groupAccessService.getGroupAccessUsers(group.organizationId, group.id)
      setGroupAccessUsers(accessUsers)
    } catch (error) {
      console.error('Error loading group access users:', error)
      toast.error('Failed to load access users')
    }
  }

  const handleGrantAccess = async () => {
    if (!accessEmail.trim()) {
      toast.error('Please enter an email address')
      return
    }

    try {
      // Find user by email
      const usersQuery = query(collection(db, 'users'), where('email', '==', accessEmail.trim()))
      const usersSnapshot = await getDocs(usersQuery)
      
      if (usersSnapshot.empty) {
        toast.error('User not found with that email address')
        return
      }

      const userDoc = usersSnapshot.docs[0]
      const userId = userDoc.id

      await groupAccessService.grantGroupAccess(
        userId,
        selectedGroup.organizationId,
        selectedGroup.id,
        selectedGroup.name,
        currentUser.uid
      )

      toast.success('Access granted successfully!')
      setAccessEmail('')
      
      // Refresh access users list
      const accessUsers = await groupAccessService.getGroupAccessUsers(selectedGroup.organizationId, selectedGroup.id)
      setGroupAccessUsers(accessUsers)
    } catch (error) {
      console.error('Error granting group access:', error)
      toast.error('Failed to grant access')
    }
  }

  const handleRevokeAccess = async (userId) => {
    try {
      await groupAccessService.revokeGroupAccess(userId, selectedGroup.organizationId, selectedGroup.id)
      toast.success('Access revoked successfully!')
      
      // Refresh access users list
      const accessUsers = await groupAccessService.getGroupAccessUsers(selectedGroup.organizationId, selectedGroup.id)
      setGroupAccessUsers(accessUsers)
    } catch (error) {
      console.error('Error revoking group access:', error)
      toast.error('Failed to revoke access')
    }
  }

  const handleOpenAddGroupModal = () => {
    console.log('🔍 Groups: Opening modal - userProfile:', userProfile)
    console.log('🔍 Groups: userProfile.organizationId:', userProfile?.organizationId)
    console.log('🔍 Groups: userProfile.organizations:', userProfile?.organizations)
    
    // Set organization for org admins when opening modal
    let orgId = userProfile?.organizationId
    
    // Fallback: check organizations array if organizationId is not set
    if (!orgId && userProfile?.organizations && userProfile.organizations.length > 0) {
      orgId = userProfile.organizations[0] // organizations is array of strings, not objects
      console.log('🔍 Groups: Using organization from organizations array:', orgId)
    }
    
    if (orgId) {
      console.log('✅ Groups: Setting organization in modal to:', orgId)
      setGroupForm(prev => ({
        ...prev,
        organizationId: orgId
      }))
    } else {
      console.log('⚠️ Groups: No organizationId found in userProfile or organizations array')
    }
    setShowAddGroupModal(true)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  // Show access denied message for standard users
  if (userProfile?.accessLevel === 'standard') {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Shield className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Restricted</h2>
          <p className="text-gray-600 mb-4">Standard users cannot create or manage groups.</p>
          <p className="text-sm text-gray-500">Upgrade your plan to access group management features.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Groups</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage and join groups within your organizations
          </p>
        </div>
        {canCreateGroups && (
          <button
            onClick={handleOpenAddGroupModal}
            className="btn-primary flex items-center space-x-2"
          >
            <Plus className="h-4 w-4" />
            <span>New Group</span>
          </button>
        )}
      </div>

      {/* Search and Filter */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search groups..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            />
          </div>
        </div>
      </div>

      {/* Groups List */}
      {filteredGroups.length === 0 ? (
        <div className="text-center py-12">
          <UserPlus className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No groups found</h3>
          <p className="text-gray-500 mb-4">
            {searchTerm 
              ? 'No groups match your search criteria'
              : 'Get started by creating your first group'
            }
          </p>
          {!searchTerm && canCreateGroups && (
            <button
              onClick={handleOpenAddGroupModal}
              className="btn-primary"
            >
              Create Group
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {filteredGroups.map((group) => (
            <div key={group.id} className="card">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="text-lg font-medium text-gray-900">{group.name}</h3>
                  <p className="text-sm text-gray-600 mt-1">{group.description || 'No description'}</p>
                  
                  <div className="mt-4 space-y-2">
                    
                    {group.contactEmail && (
                      <div className="flex items-center text-sm text-gray-500">
                        <Mail className="h-4 w-4 mr-2" />
                        <span>{group.contactEmail}</span>
                      </div>
                    )}
                    
                    {group.contactPhone && (
                      <div className="flex items-center text-sm text-gray-500">
                        <Phone className="h-4 w-4 mr-2" />
                        <span>{group.contactPhone}</span>
                      </div>
                    )}
                    
                    {group.groupLeaderEmail && (
                      <div className="flex items-center text-sm text-blue-600">
                        <UserCheck className="h-4 w-4 mr-2" />
                        <span>Leader: {group.groupLeaderEmail}</span>
                      </div>
                    )}
                  </div>

                  <div className="mt-4 flex items-center justify-between">
                    <div className="flex items-center flex-wrap gap-x-4 gap-y-1 text-sm text-gray-500">
                      <span className="flex items-center">
                        <Users className="h-4 w-4 mr-1" />
                        {group.memberCount || 0} members
                      </span>
                      <span className="flex items-center">
                        <Heart className="h-4 w-4 mr-1 text-red-400" />
                        {group.followerCount ?? 0} following
                      </span>
                      <span className="text-xs">
                        Max: {isEnterprisePlan ? 'Unlimited' : (group.maxMembers ?? 50)}
                      </span>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        group.isPrivate === true
                          ? 'bg-gray-100 text-gray-800' 
                          : 'bg-green-100 text-green-800'
                      }`}>
                        {group.isPrivate === true ? 'Private' : 'Public'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="mt-4 flex items-center justify-between">
                <div className="text-xs text-gray-500">
                  {getOrganizationName(group.organizationId)}
                </div>
                
                <div className="flex space-x-2">
                  {/* Show access management button for private groups */}
                  {group.isPrivate === true && (
                    <button 
                      onClick={() => handleManageAccess(group)}
                      className="btn-secondary text-sm px-3 py-1"
                    >
                      <Shield className="h-3 w-3 mr-1" />
                      Access
                    </button>
                  )}
                  
                  {/* Show edit/delete buttons for all groups if user is org admin */}
                  {canCreateGroups && (
                    <>
                      <button 
                        onClick={() => handleEditGroup(group)}
                        className="btn-secondary text-sm px-3 py-1"
                      >
                        <Edit className="h-3 w-3 mr-1" />
                        Edit
                      </button>
                      <button 
                        onClick={() => handleDeleteGroup(group)}
                        className="btn-secondary text-sm px-3 py-1 text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="h-3 w-3 mr-1" />
                        Delete
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Group Modal */}
      {showAddGroupModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setShowAddGroupModal(false)} />
            <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">
                  {editingGroup ? 'Edit Group' : 'Create New Group'}
                </h3>
              </div>
              
              <form onSubmit={handleSubmitGroup} className="px-6 py-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Group Name *
                  </label>
                  <input
                    type="text"
                    value={groupForm.name}
                    onChange={(e) => setGroupForm({ ...groupForm, name: e.target.value })}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                    placeholder="Enter group name"
                    required
                  />
                </div>


                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={groupForm.description}
                    onChange={(e) => setGroupForm({ ...groupForm, description: e.target.value })}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                    rows={3}
                    placeholder="Describe the group's purpose"
                  />
                </div>

                {!isEnterprisePlan && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Max Members
                  </label>
                  <input
                    type="number"
                    value={groupForm.maxMembers}
                    onChange={(e) => setGroupForm({ ...groupForm, maxMembers: e.target.value })}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                    min="1"
                    max="1000"
                  />
                </div>
                )}
                {isEnterprisePlan && (
                  <p className="text-sm text-gray-600">
                    Your Enterprise plan does not apply a per-group member cap.
                  </p>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Contact Email
                    </label>
                    <input
                      type="email"
                      value={groupForm.contactEmail}
                      onChange={(e) => setGroupForm({ ...groupForm, contactEmail: e.target.value })}
                      className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                      placeholder="group@example.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Contact Phone
                    </label>
                    <input
                      type="tel"
                      value={groupForm.contactPhone}
                      onChange={(e) => setGroupForm({ ...groupForm, contactPhone: e.target.value })}
                      className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                      placeholder="(555) 123-4567"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Group Leader Email
                  </label>
                  <input
                    type="email"
                    value={groupForm.groupLeaderEmail}
                    onChange={(e) => setGroupForm({ ...groupForm, groupLeaderEmail: e.target.value })}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                    placeholder="leader@example.com"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Assign a group leader who can post alerts to this specific group
                  </p>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="isPublic"
                    checked={groupForm.isPublic}
                    onChange={(e) => setGroupForm({ ...groupForm, isPublic: e.target.checked, isPrivate: !e.target.checked })}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <label htmlFor="isPublic" className="ml-2 block text-sm text-gray-700">
                    Make this group public (visible to all organization members)
                  </label>
                </div>

                {canCreatePrivateGroups && (
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="isPrivate"
                      checked={groupForm.isPrivate}
                      onChange={(e) => setGroupForm({ ...groupForm, isPrivate: e.target.checked, isPublic: !e.target.checked })}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <label htmlFor="isPrivate" className="ml-2 block text-sm text-gray-700">
                      <Lock className="h-4 w-4 inline mr-1" />
                      Make this group private (only invited members can see and join)
                    </label>
                  </div>
                )}

                {!canCreatePrivateGroups && (
                  <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                    <div className="flex">
                      <Lock className="h-5 w-5 text-blue-400 mr-2" />
                      <div className="text-sm text-blue-700">
                        <p className="font-medium">Private groups available with Premium/Enterprise</p>
                        <p className="text-blue-600">Upgrade to create private groups with restricted access</p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddGroupModal(false)
                      setEditingGroup(null)
                    }}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn-primary"
                  >
                    {editingGroup ? 'Update Group' : 'Create Group'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Group Access Management Modal */}
      {showAccessModal && selectedGroup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Manage Access - {selectedGroup.name}
              </h3>
              <button
                onClick={() => setShowAccessModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* Grant Access Section */}
            <div className="mb-6">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Grant Access</h4>
              <div className="flex space-x-2">
                <input
                  type="email"
                  value={accessEmail}
                  onChange={(e) => setAccessEmail(e.target.value)}
                  placeholder="Enter user email"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  onClick={handleGrantAccess}
                  className="btn-primary px-4 py-2"
                >
                  <UserCheck className="h-4 w-4 mr-1" />
                  Grant
                </button>
              </div>
            </div>

            {/* Current Access Users */}
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Current Access</h4>
              {groupAccessUsers.length === 0 ? (
                <p className="text-sm text-gray-500">No users have access to this group</p>
              ) : (
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {groupAccessUsers.map((access) => (
                    <div key={access.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                      <span className="text-sm text-gray-700">{access.userId}</span>
                      <button
                        onClick={() => handleRevokeAccess(access.userId)}
                        className="text-red-600 hover:text-red-700 text-sm"
                      >
                        Revoke
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex justify-end pt-4">
              <button
                onClick={() => setShowAccessModal(false)}
                className="btn-secondary"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
